import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Section, Segmented } from '../settingsPrimitives';
import { useAuthStore } from '../../store/authStore';
import { useEntitlementsStore } from '../../store/entitlementsStore';
import { hasCloudSync, newItemLocationDefault, setNewItemLocationDefault } from '../../plans/cloudAccess';
import { isTauri } from '../../utils/isTauri';
import { isLimitedPlan } from '../../services/personalSyncConsent';
import { formatBytes } from '../../services/planQuota';

/** What the Free plan syncs until the server has told us (GET /auth/usage). */
export const FREE_DEFAULT_LIMITS = { boards: 3, documents: 50, bytes: 5 * 1024 * 1024 };

/** Limits from /auth/usage (`null` = unlimited), falling back to the Free defaults. */
export function personalLimits(usage) {
  const l = usage?.limits;
  if (!l || !('personal_boards' in l)) return FREE_DEFAULT_LIMITS;
  return { boards: l.personal_boards, documents: l.personal_documents, bytes: l.personal_sync_bytes };
}

/**
 * Settings → Plan: what this account may keep in the cloud, and how much of it
 * is used.
 *
 * THE ON/OFF SWITCH IS GONE (card C9). It asked the wrong question: sync is
 * not a mode any more, it is a property of each board and document — cloud, or
 * this device — chosen from the item's own menu, with new items going to the
 * cloud until the plan is full. A switch on top of that could only contradict
 * it. Nothing here writes; it reports.
 */
export default function PersonalSyncSection() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const fetchUsage = useAuthStore((s) => s.fetchUsage);
  const usage = user?._usage;
  // Re-render on a plan / capability change (cloud sync decides what is shown).
  useEntitlementsStore((s) => `${s.plan}|${s.capabilitiesKnown}|${(s.capabilities || []).join(',')}`);

  useEffect(() => {
    if (user?.id != null) fetchUsage?.();
    // Once per account/plan, not on every usage write back into the user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.plan]);

  if (user?.id == null) return null;

  const limits = personalLimits(usage);
  const cap = (n, fmt = String) => (n == null || n < 0 ? t('personalSync.unlimited') : fmt(n));
  const used = usage?.usage?.personal_sync;

  return (
    <Section title={t('personalSync.sectionTitle')}>
      {/* Without cloud sync on the desktop, NewItemLocation says where things go instead. */}
      {!(isTauri() && !hasCloudSync()) && <div className="settings-row-desc" style={{ padding: '0 0 10px' }}>
        {isLimitedPlan(user)
          ? t('personalSync.descCloudLimits', {
              boards: cap(limits.boards),
              documents: cap(limits.documents),
              storage: cap(limits.bytes, formatBytes),
            })
          : t('personalSync.descPaid')}
      </div>}
      {used && (
        <div className="settings-row-desc" data-testid="personal-sync-usage" style={{ padding: '0 0 10px' }}>
          {t('personalSync.usage', {
            boards: used.boards,
            boardsLimit: cap(limits.boards),
            documents: used.documents,
            documentsLimit: cap(limits.documents),
            storage: formatBytes(used.bytes),
            storageLimit: cap(limits.bytes, formatBytes),
          })}
        </div>
      )}
      <FileStorageUsage usage={usage} t={t} />
      <NewItemLocation t={t} userId={user.id} />
    </Section>
  );
}

/**
 * Plans v2 card 3.4: where NEW workspaces, boards and documents go. Offered
 * with `cloud_sync` (Pro, Team); without it everything stays on this device
 * and the section says so.
 */
function NewItemLocation({ t, userId }) {
  const [value, setValue] = useState(() => newItemLocationDefault(String(userId)));
  useEffect(() => setValue(newItemLocationDefault(String(userId))), [userId]);
  if (!isTauri()) return null;
  if (!hasCloudSync()) {
    return (
      <div className="settings-row-desc" data-testid="new-item-location-no-cloud" style={{ padding: '0 0 10px' }}>
        {t('cloudLocal.noCloud')}
      </div>
    );
  }
  return (
    <Row label={t('cloudLocal.default.label')} desc={t('cloudLocal.default.desc')}>
      <Segmented
        value={value}
        onChange={(next) => {
          if (setNewItemLocationDefault(next, String(userId))) setValue(next);
        }}
        options={[
          { id: 'cloud', label: t('cloudLocal.default.cloud') },
          { id: 'device', label: t('cloudLocal.default.device') },
        ]}
      />
    </Row>
  );
}

/**
 * Chat attachments and document images against the plan's file limits
 * (backend Plans.FileQuota: Free 1 GB total, 5 MB per file).
 */
function FileStorageUsage({ usage, t }) {
  const limits = usage?.limits;
  const used = usage?.usage?.file_storage;
  if (!limits || !('file_storage_bytes' in limits) || !used) return null;
  const cap = (n) => (n == null || n < 0 ? t('personalSync.unlimited') : formatBytes(n));
  return (
    <div className="settings-row-desc" data-testid="file-storage-usage" style={{ padding: '0 0 10px' }}>
      {t('personalSync.storageUsage', {
        used: formatBytes(used.bytes),
        limit: cap(limits.file_storage_bytes),
        perFile: cap(limits.file_max_bytes),
      })}
    </div>
  );
}
